import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
} from "mobx"

import { catchErrors, currenciesApi } from "@src/shared/api"
import {
  NetworkAll,
  NetworkCrypto,
  SymbolAll,
  SymbolCrypto,
} from "@src/shared/api/generated"
import Collection from "@src/shared/classes/Collection/Collection"
import { loading } from "@src/shared/classes/utils/loading"
import { wrapSingleton } from "@src/shared/classes/utils/wrapSingleton"

export type Currency = {
  decimals: number
  display_name: string
  id: string
  network: NetworkAll | NetworkCrypto
  symbol: SymbolAll | SymbolCrypto
  symbol_alias: SymbolAll | SymbolCrypto
  token_kind: null | string
  tx_explorer_url: string
}
export class CurrenciesCollection extends loading(
  ["load"],
  Collection<Currency>,
) {
  constructor() {
    super()

    makeObservable(this, {
      crypto: computed,
      cryptoNetworks: computed,
      fiat: computed,
      load: action.bound,
      networks: computed,
      selectOptions: computed,
      selectOptionsCrypto: computed,
      selectOptionsFiat: computed,
      symbols: computed,
    })

    this.load()
  }

  getBySymbolNetwork({ network, symbol }: { network: string; symbol: string }) {
    return this.items.find((i) => i.network === network && i.symbol === symbol)
  }

  async load() {
    try {
      const data = await Promise.allSettled([
        currenciesApi.getCurrencies(),
        currenciesApi.getCurrencies("fiat"),
        currenciesApi.getCurrencies("card"),
        currenciesApi.getCurrencies("bank"),
      ])

      const result = data
        .map((d) => {
          if (d.status === "rejected") {
            return []
          }

          return d.value.data.data
        })
        .flat()

      if (result) {
        this.replace(
          (result as Currency[]).map((c) => ({
            ...c,
            id: `${c.symbol}_${c.network}_${c.token_kind}_${c.decimals}`,
          })),
        )
      }
    } catch (e: any) {
      catchErrors(e)
    }
  }

  get crypto() {
    return this.items.filter(
      (i) => !["bank", "card", "fiat"].includes(i.network),
    )
  }

  get cryptoNetworks() {
    return [...new Set(this.items.map((i) => i.network))].filter(
      (i) => i !== "fiat",
    )
  }

  get fiat() {
    return this.items.filter((i) => i.network === "fiat")
  }

  get networks() {
    return [...new Set(this.items.map((i) => i.network))]
  }

  get selectOptions() {
    return this.items
      .slice()
      .sort((a, b) => (a.symbol > b.symbol ? 1 : 0))
      .map(({ network, symbol, symbol_alias }) => ({
        label: `${symbol_alias} (${network})`,
        value: `${symbol},${network}`,
      }))
  }

  get selectOptionsCrypto() {
    return this.crypto
      .slice()
      .sort((a, b) => (a.symbol > b.symbol ? 1 : 0))
      .map(({ network, symbol, symbol_alias }) => ({
        label: `${symbol_alias} (${network})`,
        value: `${symbol},${network}`,
      }))
  }
  get selectOptionsFiat() {
    return this.fiat
      .slice()
      .sort((a, b) => (a.symbol > b.symbol ? 1 : 0))
      .map(({ network, symbol, symbol_alias }) => ({
        label: `${symbol_alias}`,
        value: symbol,
      }))
  }

  get symbols() {
    return [...new Set(this.items.map((i) => i.symbol))]
  }
}

export const GlobalCurrenciesCollection = wrapSingleton(CurrenciesCollection)
